<template lang="pug">
.split(ref="root")
  el-button.close-doc-btn(
    v-if="closeVisible"
    type="info"
    icon="el-icon-close"
    circle
    size="mini"
    @click="$emit('close')"
    title="Fechar"
  )
  .pane.left(:style="{ width: leftWidth + 'px' }")
    slot(name="left")

  .divider
    .handle(
      role="separator"
      aria-orientation="vertical"
      @mousedown.prevent="startDrag"
      @touchstart.prevent="startDragTouch"
    )

  .pane.right
    slot(name="right")
</template>

<script>
export default {
  name: 'SplitPane',
  props: {
    initialLeft: { type: Number, default: 320 },
    minLeft: { type: Number, default: 0 },
    minRight: { type: Number, default: 0 },
    storageKey: { type: String, default: '' },
    startRightCollapsed: { type: Boolean, default: false },
    closeVisible: { type: Boolean, default: false }
  },
  data () {
    return {
      leftWidth: this.initialLeft,
      dragging: false,
      rootLeft: 0,
      rootWidth: 0
    }
  },
  mounted () {
    if (this.storageKey) {
      const saved = Number(localStorage.getItem(this.storageKey))
      if (!Number.isNaN(saved) && saved > 0) {
        this.leftWidth = saved
      }
    }

    this.$nextTick(() => {
      this.measure()

      if (this.startRightCollapsed) {
        this.leftWidth = this.rootWidth
      }

      this.clampToContainer()
    })
    window.addEventListener('resize', this.clampToContainer)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.clampToContainer)
    this.stopDrag()
  },
  methods: {
    measure () {
      const el = this.$refs.root
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.rootLeft = rect.left
      this.rootWidth = rect.width
    },

    clampToContainer () {
      this.measure()
      const maxLeft = this.rootWidth - this.minRight
      this.leftWidth = Math.min(
        Math.max(this.leftWidth, this.minLeft),
        maxLeft
      )
    },

    startDrag () {
      this.dragging = true
      this.measure()
      document.body.classList.add('split-dragging')

      window.addEventListener('mousemove', this.onMove)
      window.addEventListener('mouseup', this.stopDrag)
    },

    startDragTouch () {
      this.dragging = true
      this.measure()
      document.body.classList.add('split-dragging')

      window.addEventListener('touchmove', this.onMoveTouch)
      window.addEventListener('touchend', this.stopDrag)
      window.addEventListener('touchcancel', this.stopDrag)
    },

    onMove (e) {
      if (!this.dragging) return
      e.preventDefault()
      const x = e.clientX - this.rootLeft
      this.setLeftWidth(x)
    },

    onMoveTouch (e) {
      if (!this.dragging) return
      const t = e.touches && e.touches[0]
      if (!t) return
      const x = t.clientX - this.rootLeft
      this.setLeftWidth(x)
    },

    setLeftWidth (x) {
      const maxLeft = this.rootWidth - this.minRight
      const clamped = Math.min(Math.max(x, this.minLeft), maxLeft)

      this.leftWidth = clamped

      this.$emit('resize', {
        left: this.leftWidth,
        right: this.rootWidth - this.leftWidth
      })

      if (this.storageKey) {
        localStorage.setItem(this.storageKey, String(this.leftWidth))
      }
    },

    stopDrag () {
      if (!this.dragging) return

      this.dragging = false
      document.body.classList.remove('split-dragging')

      window.removeEventListener('mousemove', this.onMove)
      window.removeEventListener('mouseup', this.stopDrag)

      window.removeEventListener('touchmove', this.onMoveTouch)
      window.removeEventListener('touchend', this.stopDrag)
      window.removeEventListener('touchcancel', this.stopDrag)
    },

    expandLeftFull () {
      this.$nextTick(() => {
        this.measure()
        this.leftWidth = this.rootWidth

        this.$emit('resize', {
          left: this.leftWidth,
          right: 0
        })

        if (this.storageKey) {
          localStorage.setItem(this.storageKey, String(this.leftWidth))
        }
      })
    },

    expandHalf () {
      this.$nextTick(() => {
        this.measure()

        const metade = this.rootWidth / 2
        this.leftWidth = metade

        this.$emit('resize', {
          left: this.leftWidth,
          right: this.rootWidth - this.leftWidth
        })

        if (this.storageKey) {
          localStorage.setItem(this.storageKey, String(this.leftWidth))
        }
      })
    }
  }
}
</script>

<style scoped>
.split {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.close-doc-btn{
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 20;

  background: #f0f0f0;
  border-color: #dcdcdc;
  color: #606266;
}

.pane {
  height: 100%;
  overflow: auto;
  min-width: 0;
}

.left {
  flex: 0 0 auto;
}

.right {
  flex: 1 1 auto;
}

.divider {
  flex: 0 0 4px;
  position: relative;
  background: rgba(0, 0, 0, 0.06);
}

.handle {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  height: 20px;
  cursor: col-resize;

  background: rgba(0, 0, 0, 0.10);
}

.handle::before {
  content: "⟷";
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.6);
}

.divider:hover {
  background: rgba(0, 0, 0, 0.12);
}
</style>

<style>
.split-dragging {
  user-select: none;
  cursor: col-resize !important;
}
</style>



// WEBPACK FOOTER //
// src/screens/analise/components/AppSplitPane.vue